/**
 * Client-safe shared types + pure semver helpers used by BOTH the
 * server-only reader (lib/dependency-snapshot.ts) and the client
 * dashboard. No fs / process / server-only imports here — this module
 * is bundled into the browser.
 */

export type DependencySnapshot = {
  name: string
  declared: string
  kind: "dep" | "dev"
}

export type UpdateStatus = "current" | "patch" | "minor" | "major" | "unknown"

export function stripRange(declared: string): string {
  return declared.replace(/^[\^~=>< ]+/, "").trim()
}

export function compareSemver(
  declared: string,
  latest: string,
): UpdateStatus {
  const d = parseSemver(stripRange(declared))
  const l = parseSemver(latest)
  if (!d || !l) return "unknown"
  if (l.major > d.major) return "major"
  if (l.minor > d.minor) return "minor"
  if (l.patch > d.patch) return "patch"
  return "current"
}

function parseSemver(v: string) {
  // Accept short-form declarations like "3" or "8" (common for eslint,
  // postcss, etc.) by padding missing segments with zeros. Previously
  // we required X.Y.Z and anything shorter fell through to "unknown",
  // which left a permanent "En cours…" count on the dashboard.
  const match = v.match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?/)
  if (!match) return null
  return {
    major: Number(match[1]),
    minor: Number(match[2] ?? 0),
    patch: Number(match[3] ?? 0),
  }
}
